import { createSelector, createSlice } from '@reduxjs/toolkit';

const emailMarketingSlice = createSlice({
    initialState: {
        campaigns: {
            list: [],
            total: 0,
            page: 0,
            pageSize: 20,
            isLoading: false,
        },
        currentCampaign: {
            data: null,
            isLoading: false,
            error: null,
        },
        dryRun: {
            count: 0,
            recipients: [],
            isLoading: false,
            isApproximate: false,
        },
        send: {
            isLoading: false,
            result: null,
            error: null,
        },
        unsubscribes: {
            list: [],
            total: 0,
            isLoading: false,
        },
        summary: {
            data: null,
            isLoading: false,
        },
    },
    name: 'emailMarketing',
    reducers: {
        // Campaigns list
        fetchCampaigns: state => {
            state.campaigns.isLoading = true;
        },
        campaignsFetched: (state, { payload }) => {
            state.campaigns.isLoading = false;
            state.campaigns.list = payload.data;
            state.campaigns.total = payload.total;
            state.campaigns.page = payload.page;
            state.campaigns.pageSize = payload.pageSize;
        },
        campaignsFetchFailed: state => {
            state.campaigns.isLoading = false;
        },

        // Single campaign
        fetchCampaign: state => {
            state.currentCampaign.isLoading = true;
            state.currentCampaign.data = null;
            state.currentCampaign.error = null;
        },
        campaignFetched: (state, { payload }) => {
            state.currentCampaign.isLoading = false;
            state.currentCampaign.data = payload;
            state.currentCampaign.error = null;
        },
        campaignFetchFailed: (state, { payload }) => {
            state.currentCampaign.isLoading = false;
            state.currentCampaign.error =
                payload?.error || 'Failed to load campaign';
        },

        // Summary
        fetchSummary: state => {
            state.summary.isLoading = true;
        },
        summaryFetched: (state, { payload }) => {
            state.summary.isLoading = false;
            state.summary.data = payload;
        },
        summaryFetchFailed: state => {
            state.summary.isLoading = false;
        },

        // Send campaign
        sendCampaign: state => {
            state.send.isLoading = true;
            state.send.error = null;
        },
        campaignSent: (state, { payload }) => {
            state.send.isLoading = false;
            state.send.result = payload;
        },
        campaignSendFailed: (state, { payload }) => {
            state.send.isLoading = false;
            state.send.error = payload?.error || 'Failed to send campaign';
        },

        // Dry run
        runDryRun: state => {
            state.dryRun.isLoading = true;
        },
        dryRunCompleted: (state, { payload }) => {
            state.dryRun.isLoading = false;
            state.dryRun.count = payload.count;
            state.dryRun.recipients = payload.recipients;
            state.dryRun.isApproximate = !!payload.isApproximate;
        },
        dryRunFailed: state => {
            state.dryRun.isLoading = false;
            state.dryRun.count = 0;
            state.dryRun.recipients = [];
            state.dryRun.isApproximate = false;
        },
        resetDryRun: state => {
            state.dryRun.count = 0;
            state.dryRun.recipients = [];
            state.dryRun.isLoading = false;
            state.dryRun.isApproximate = false;
        },

        // Unsubscribes
        fetchUnsubscribes: state => {
            state.unsubscribes.isLoading = true;
        },
        unsubscribesFetched: (state, { payload }) => {
            state.unsubscribes.isLoading = false;
            state.unsubscribes.list = payload.data;
            state.unsubscribes.total = payload.total;
        },
        unsubscribesFetchFailed: state => {
            state.unsubscribes.isLoading = false;
        },

        addUnsubscribe: () => {},
        removeUnsubscribe: () => {},

        // Cancel, duplicate, schedule, test-send
        cancelCampaign: () => {},
        duplicateCampaign: () => {},
        scheduleCampaign: () => {},
        testSendCampaign: () => {},

        // Reset send state
        resetSendState: state => {
            state.send.isLoading = false;
            state.send.result = null;
            state.send.error = null;
        },
    },
});

// Selectors
const getSelf = state => state.emailMarketing;

export const getCampaigns = createSelector(getSelf, s => s.campaigns);
export const getCurrentCampaign = createSelector(
    getSelf,
    s => s.currentCampaign,
);
export const getDryRun = createSelector(getSelf, s => s.dryRun);
export const getSendState = createSelector(getSelf, s => s.send);
export const getUnsubscribes = createSelector(getSelf, s => s.unsubscribes);
export const getEmailSummary = createSelector(getSelf, s => s.summary);

// Export actions
export const {
    fetchCampaigns,
    campaignsFetched,
    campaignsFetchFailed,
    fetchCampaign,
    campaignFetched,
    campaignFetchFailed,
    fetchSummary,
    summaryFetched,
    summaryFetchFailed,
    sendCampaign,
    campaignSent,
    campaignSendFailed,
    runDryRun,
    dryRunCompleted,
    dryRunFailed,
    resetDryRun,
    fetchUnsubscribes,
    unsubscribesFetched,
    unsubscribesFetchFailed,
    addUnsubscribe,
    removeUnsubscribe,
    cancelCampaign,
    duplicateCampaign,
    scheduleCampaign,
    testSendCampaign,
    resetSendState,
} = emailMarketingSlice.actions;

export default emailMarketingSlice.reducer;
